import React, { useEffect, useMemo, useState } from 'react'
import DocumentTitle from 'react-document-title'
import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Sentry from '@sentry/browser'
import { Scope } from '@sentry/hub'
import { AxiosError, AxiosResponse } from 'axios'
import cn from 'classnames'
import { isValidNumber } from 'libphonenumber-js'
import Cookies from 'universal-cookie'
import * as yup from 'yup'

import selector from './selector'
import useStyles from './styles'
import { IFormFields, SignupFields } from './types'

import {
  GA4_ACTIONS,
  GA4_PLACES,
  getGACookies,
  getUTMParams,
  hasGLParam,
  onSendGA4Event,
  sendGA4Event,
} from '@/analytics'
import { fbPixel } from '@/analytics/facebook-pixel'
import { ErrorResponseType } from '@/common/types'
import { ALERT_VARIANT, AlertInline } from '@/components/AlertInlineSMB'
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '@/components/ButtonSMB'
import { CheckboxControlled } from '@/components/CheckboxControlled'
import { ComboBox, COMBOBOX_SIZE, IComboBoxOption } from '@/components/ComboBox'
import { FieldHelpText } from '@/components/FieldHelpText'
import { InputControlled } from '@/components/InputControlled'
import { InputPhoneControlled } from '@/components/InputPhoneControlled/InputPhoneControlled'
import { INPUT_SIZE, INPUT_VARIANT } from '@/components/InputSMB'
import SupportFooter from '@/components/SupportFooter'
import { EMAIL_REGEX, GLOBAL_COUNTRIES } from '@/const'
import { links } from '@/const/links'
import { Routes } from '@/const/routes'
import { MyThunkDispatch } from '@/store/store'
import { getCountries, registerUser, setMailProvider, setUserTokens } from '@/store/user'
import { ISignupResponse } from '@/store/user/types'
import { setToStorage } from '@/utils/localStorage'

const Signup = () => {
  const { authorized, language, countries, isCountriesLoading } = useSelector(selector)
  const [nonFieldError, setNonFieldError] = useState('')

  const dispatch: MyThunkDispatch = useDispatch()
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const promo = id || ''

  const schema = yup.object().shape({
    first_name: yup.string().required(formatMessage({ id: 'signUp.root.nameError' })),
    phone: yup
      .string()
      .required(formatMessage({ id: 'common.requiredField' }))
      .test('is-valid-phone', formatMessage({ id: 'common.error.phoneValidation' }), (value) => {
        if (!value) {
          return false
        }

        return isValidNumber(`+${value}`)
      }),
    email: yup
      .string()
      .matches(EMAIL_REGEX, formatMessage({ id: 'signUp.root.emailError' }))
      .required(formatMessage({ id: 'common.requiredField' })),
    password: yup.string().required(formatMessage({ id: 'signUp.root.passwordError' })),
    password_repeat: yup
      .string()
      .oneOf([yup.ref('password')], formatMessage({ id: 'signUp.root.repeatPasswordError' }))
      .required(formatMessage({ id: 'common.requiredField' })),
    terms: yup
      .boolean()
      .default(false)
      .when('country', ([country], schema) => (country !== GLOBAL_COUNTRIES.RU ? schema.oneOf([true]) : schema)),
    country: yup.string().required(formatMessage({ id: 'profile.form.selectCountry' })),
  })

  const {
    control,
    formState: { errors, isSubmitted },
    handleSubmit,
    watch,
    setValue,
    setError,
  } = useForm<IFormFields>({
    shouldFocusError: false,
    defaultValues: {
      first_name: '',
      email: '',
      password: '',
      password_repeat: '',
      terms: false,
      country: '',
      phone: '',
    },
    resolver: yupResolver(schema),
  })

  const country = watch('country')

  const trackGACookiesAndUTM = (email: string) => {
    const gaCookies = getGACookies()
    const utmParams = getUTMParams()

    sendGA4Event({
      action: GA4_ACTIONS.TEST_GA_CLIENT_ID,
      ga: gaCookies,
      utm: utmParams,
      email,
    })
  }

  useEffect(() => {
    const utmParams = getUTMParams()
    const hasGL = hasGLParam()

    if (!utmParams.length && !hasGL) {
      setToStorage('utmIsEmpty', true)
    }

    if (authorized) {
      history.push(Routes.INDEX)
    } else {
      sendGA4Event({ action: GA4_ACTIONS.SIGNUP_VISIT, place: GA4_PLACES.SIGNUP })
    }

    if (location.search) {
      const queryEmail = new URLSearchParams(location.search).get('email')

      if (queryEmail) {
        setValue('email', queryEmail)
      }
    }
  }, [])

  useEffect(() => {
    dispatch(getCountries(language))
  }, [language])

  const isSanctionCountry = useMemo(() => country === GLOBAL_COUNTRIES.RU, [country])

  const countryHandler = ({ value }: IComboBoxOption) => {
    sendGA4Event({ action: GA4_ACTIONS.SIGNUP_COUNTRY_CLICK, place: GA4_PLACES.SIGNUP })

    if (value === GLOBAL_COUNTRIES.RU) {
      sendGA4Event({ action: GA4_ACTIONS.SIGNUP_WARNING, place: GA4_PLACES.SIGNUP, text: 'VAX' })
    }
    if (value === GLOBAL_COUNTRIES.GB) {
      sendGA4Event({ action: GA4_ACTIONS.SIGNUP_WARNING, place: GA4_PLACES.SIGNUP, text: 'Sanctions' })
    }

    setValue('country', value, { shouldValidate: true })
  }

  const onSubmit = handleSubmit((data, e) => {
    e?.preventDefault()

    const { email, first_name, password, password_repeat, phone } = data
    sendGA4Event({ action: GA4_ACTIONS.SIGNUP_TRY_TO_FINISH, place: GA4_PLACES.SIGNUP })

    if (isSanctionCountry) {
      return
    }

    const cookies = new Cookies(document.cookie)

    dispatch(
      registerUser({
        first_name: first_name,
        email: email.toLowerCase(),
        fbc: cookies.get('_fbc') || '',
        fbp: cookies.get('_fbp') || '',
        password: password,
        password_repeat: password_repeat,
        referer: document.referrer,
        preferred_language: language,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        phone,
        country,
        promo,
      })
    )
      .then((response: AxiosResponse<ISignupResponse>) => {
        if (response.status === 201) {
          trackGACookiesAndUTM(email)
          sendGA4Event({ action: GA4_ACTIONS.SIGNUP_SUCCESS, place: GA4_PLACES.SIGNUP })
          sendGA4Event({ action: GA4_ACTIONS.SIGNUP_SUBMIT })

          fbPixel.track('SubmitApplication')

          dispatch(setMailProvider(email.toLowerCase()))
          dispatch(setUserTokens(response.headers['aitarget-access'], response.headers['aitarget-refresh']))

          window.location.reload()
        } else {
          return Promise.reject(response)
        }

        return response
      })
      .catch((error: AxiosError<ErrorResponseType>) => {
        const response = error.response

        if (response?.status === 403) {
          // 403 error happens, if employee authorized in admin interface
          setNonFieldError(formatMessage({ id: 'common.adminError' }))
        } else if ((response && response.status >= 500) || response === undefined) {
          setNonFieldError(formatMessage({ id: 'common.requestError' }))
        } else {
          if (response?.data) {
            Object.keys(response.data).forEach((field: string) => {
              const key = field as SignupFields
              setError(key, { message: response.data[field].join('. ') })
            })
          }

          if (response?.status === 400) {
            Sentry.withScope(() => {
              Sentry.configureScope((scope: Scope) => [scope.setUser({ email: email.toLocaleLowerCase() })])
              Sentry.setExtra('response', response)

              Sentry.captureMessage(`Ошибка регистрации`, Sentry.Severity.Info)
            })
          }
        }

        Sentry.configureScope((scope: Scope) => {
          scope.setExtra('response', error.response)
        })

        return Promise.reject(error)
      })
  })

  const termsLink = country === GLOBAL_COUNTRIES.AZ ? links[language].evidoTermsPdfAz : links[language].evidoTermsPdf

  return (
    <DocumentTitle title={formatMessage({ id: 'signUp.root.pageTitle' })}>
      <div className={classes.root}>
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.title}>
              <FormattedMessage id="signUp.root.title" />
            </div>
          </div>
          <form onSubmit={onSubmit}>
            <div className={classes.inputGroup}>
              <InputControlled
                control={control}
                name="first_name"
                label={formatMessage({ id: 'signUp.root.nameLabel' })}
                placeholder={formatMessage({ id: 'signUp.root.name' })}
                variant={INPUT_VARIANT.grey}
                size={INPUT_SIZE.size48}
                fullWidth
              />
              <InputControlled
                control={control}
                name="email"
                label={formatMessage({ id: 'signUp.root.emailLabel' })}
                placeholder={formatMessage({ id: 'signUp.root.email' })}
                variant={INPUT_VARIANT.grey}
                size={INPUT_SIZE.size48}
                fullWidth
              />
            </div>
            <div className={classes.inputGroup}>
              <div>
                <InputControlled
                  control={control}
                  name="password"
                  label={formatMessage({ id: 'signUp.root.passwordLabel' })}
                  placeholder={formatMessage({ id: 'signUp.root.password' })}
                  variant={INPUT_VARIANT.grey}
                  size={INPUT_SIZE.size48}
                  fullWidth
                  type="password"
                />
                {nonFieldError && (
                  <FieldHelpText isError={true} className={classes.error}>
                    {nonFieldError}
                  </FieldHelpText>
                )}
              </div>

              <InputControlled
                control={control}
                name="password_repeat"
                label={formatMessage({ id: 'signUp.root.repeatPasswordLabel' })}
                placeholder={formatMessage({ id: 'signUp.root.repeatPassword' })}
                variant={INPUT_VARIANT.grey}
                size={INPUT_SIZE.size48}
                fullWidth
                type="password"
              />
            </div>
            <div className={classes.countryWrapper}>
              <div className={classes.country}>
                <div>
                  <Controller
                    name="country"
                    control={control}
                    render={() => {
                      return (
                        <ComboBox
                          options={countries}
                          onChange={countryHandler}
                          size={COMBOBOX_SIZE.big}
                          placeholder={formatMessage({ id: 'profile.form.selectCountry' })}
                          noOptionsMessage={formatMessage({ id: 'profile.form.comboBoxNoResults' })}
                          loadingMessage={formatMessage({ id: 'profile.form.comboBoxLoading' })}
                          label={formatMessage({ id: 'common.country' })}
                          isLoading={isCountriesLoading}
                          errorText={errors.country?.message}
                          searchFields={['label']}
                        />
                      )
                    }}
                  />
                </div>
                <InputPhoneControlled
                  control={control}
                  country={country.toLowerCase()}
                  name="phone"
                  title={formatMessage(
                    { id: 'profile.form.phone' },
                    {
                      detail: (
                        <span className={classes.phoneDetail}>
                          (<FormattedMessage id="profile.form.phoneDetail" />)
                        </span>
                      ),
                    }
                  )}
                />
              </div>
              <div className={classes.alerts}>
                {country === GLOBAL_COUNTRIES.GB && (
                  <AlertInline
                    variant={ALERT_VARIANT.neutralColor}
                    description={formatMessage({ id: 'profile.form.GBHintVAT' })}
                    isFullWidth
                  />
                )}
                {country === GLOBAL_COUNTRIES.RU && (
                  <AlertInline
                    variant={ALERT_VARIANT.negativeColor}
                    description={formatMessage({ id: 'profile.form.RUHint' })}
                    isFullWidth
                  />
                )}
              </div>
            </div>

            {!!country && !isSanctionCountry && (
              <div className={classes.checkboxGroup}>
                <div className={classes.terms}>
                  <CheckboxControlled control={control} name="terms">
                    <span className={cn({ [classes.termsError]: isSubmitted && errors.terms })}>
                      <FormattedMessage id="signUp.root.confirm" />{' '}
                      <a
                        target="_blank"
                        href={termsLink}
                        className={classes.link}
                        rel="noopener noreferrer"
                        onClick={onSendGA4Event({ action: GA4_ACTIONS.SIGNUP_POLITICS_CLICK })}
                      >
                        <FormattedMessage id="signUp.root.oferta" />
                      </a>{' '}
                      <FormattedMessage id="signUp.root.and" />{' '}
                      <a
                        target="_blank"
                        href={links[language].evidoPolicyPdf}
                        className={classes.link}
                        rel="noopener noreferrer"
                        onClick={onSendGA4Event({ action: GA4_ACTIONS.SIGNUP_POLITICS_CLICK })}
                      >
                        <FormattedMessage id="signUp.root.policy" />
                      </a>
                    </span>
                  </CheckboxControlled>
                </div>
              </div>
            )}
            <div className={classes.buttonGroup}>
              <Button
                fullWidth={true}
                size={BUTTON_SIZE.size40}
                type={BUTTON_TYPE.submit}
                label={formatMessage({ id: 'signUp.root.register' })}
                id="signup-submit"
                disabled={isSanctionCountry}
              />
            </div>
          </form>
        </div>
        <SupportFooter signIn />
      </div>
    </DocumentTitle>
  )
}

export default Signup
